<script setup>
import {defineProps, defineEmits, ref, computed, onMounted, defineExpose, nextTick} from 'vue'
import Multiselect from '@vueform/multiselect'
import {useNotification} from '@kyvg/vue3-notification'
import {debounce} from '@shared/utils/debounce'
import PlatformIcon from '@shared-components/ui/PlatformIcon.vue'
import {useMetaAdAccountStore} from '@/views/assets/ad-accounts/stores/MetaAdAccountStore'

const {notify} = useNotification()
const {getFacebookAdAccounts} = useMetaAdAccountStore()

// Emits & props
const emit = defineEmits(['changed', 'update:modelValue', 'focusout', 'select-option', 'get-options'])
const props = defineProps({
  modelValue: {},
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loadOnInit: {
    type: Boolean,
    default: true,
  },
  placeholder: {
    type: String,
    default: 'Select Ad Accounts',
  },
  listBySearch: {
    type: Boolean,
    default: false,
  },
  payload: {
    type: Object,
    default: null,
  },
  valueToReturn: {
    type: String,
    default: 'id',
  },
  multiple: {
    type: Boolean,
    default: false,
  },
})

// Refs
const adAccounts = ref([])
const loading = ref(false)
const mode = ref('single')
// The API returns the 50 latest accounts per request, so the full list is kept to restore it
// after a server-side search is cleared without re-fetching.
const fullSearchResultList = ref([])
const MIN_SEARCH_LENGTH = 1

// Computed
const isValid = computed(() => {
  if (props.multiple === true) {
    return !props.required || props.modelValue.length !== 0
  }

  return !props.required || !!props.modelValue
})

const getAccountValue = account => (props.valueToReturn === 'id' ? account?.id : account?.external_id)

const adAccountsOptions = computed(() => {
  return adAccounts.value.map(account => ({
    value: getAccountValue(account),
    label: account?.title,
    sublabel: account?.external_id,
    icon: account?.platform ?? '',
  }))
})

// Methods
onMounted(async () => {
  await init()
})

const init = async () => {
  if (props.multiple === true) {
    mode.value = 'tags'
  }

  if (props.loadOnInit) {
    await requestGetAdAccounts()
  }
}

const requestGetAdAccounts = async (search = '') => {
  loading.value = true

  try {
    let data = await getFacebookAdAccounts({search, ...props.payload, with: ['company', 'business_manager']})

    adAccounts.value = data.data

    if (search !== '') {
      return
    }

    fullSearchResultList.value = cloneSafe(adAccounts.value)

    // Only the full list is announced: consumers (BM Accounts Migration) preselect every option.
    await nextTick()
    emit('get-options', adAccountsOptions.value)
  } catch (responseError) {
    notify({type: 'error', text: responseError?.data.message})
  } finally {
    loading.value = false
  }
}

const reloadAdAccounts = debounce(async searchValue => {
  await requestGetAdAccounts(searchValue)
}, 300)

const restoreFullList = () => {
  // Keep the selected account in the list so the single label can still resolve it.
  const selectedAccounts = adAccounts.value.filter(account => isSelectedValue(getAccountValue(account)))
  const restoredAccounts = cloneSafe(fullSearchResultList.value)

  selectedAccounts.forEach(account => {
    if (!restoredAccounts.some(restoredAccount => getAccountValue(restoredAccount) === getAccountValue(account))) {
      restoredAccounts.push(account)
    }
  })

  adAccounts.value = restoredAccounts
}

const isSelectedValue = value => {
  if (props.multiple === true) {
    return (props.modelValue ?? []).includes(value)
  }

  return props.modelValue === value
}

// Options must not change while an option click is in flight: the input blurs before the
// click lands, and swapping the list at that moment selects a wrong option or nothing.
// That is why clear-on-blur is off; the full list is restored when the search is cleared
// (Multiselect clears it after a select, or the user empties the field).
const handleSearch = searchValue => {
  const trimmedSearch = (searchValue ?? '').trim()

  if (trimmedSearch === '') {
    restoreFullList()

    return
  }

  if (trimmedSearch.length >= MIN_SEARCH_LENGTH) {
    reloadAdAccounts(trimmedSearch)
  }
}

const findAccountByValue = value => adAccounts.value.find(account => getAccountValue(account) === value) ?? null

const onChange = value => {
  emit('update:modelValue', value)

  if (props.multiple === true) {
    emit('changed', (value ?? []).map(findAccountByValue).filter(Boolean))

    return
  }

  emit('changed', findAccountByValue(value))
}

const getActiveOption = (value, option) => {
  emit('select-option', option)
}

defineExpose({requestGetAdAccounts})
</script>

<template>
  <div :class="['the-select-wrapper', isValid ? 'valid' : 'invalid', mode === 'tags' ? 'tags' : '']">
    <input v-if="required && !isValid" class="d-none" required />
    <Multiselect
      :value="modelValue"
      ref="multiSelect"
      :options="adAccountsOptions"
      @select="getActiveOption"
      :disabled="disabled"
      :track-by="['sublabel', 'label']"
      label="label"
      :mode="mode"
      searchable
      :filter-results="false"
      :clear-on-blur="false"
      :placeholder="listBySearch ? 'Select Ad Account' : placeholder"
      :loading="loading"
      @input="onChange"
      @search-change="handleSearch"
    >
      <template #clear><span /></template>

      <template #singlelabel="{value}">
        <div class="multiselect-single-label">
          <platform-icon :platform="value.icon" class="me-2" />
          <span class="multiselect-single-label-text text-start" dir="rtl">
            <bdi dir="ltr">{{ value.label }}</bdi>
          </span>
        </div>
      </template>
      <template #option="props">
        <platform-icon :platform="props.option.icon" class="me-2" />
        <div>
          <div>{{ props.option.label }}</div>
          <small v-if="props.option.sublabel" class="text-muted">{{ props.option.sublabel }}</small>
        </div>
      </template>
    </Multiselect>
  </div>
</template>
